import { useAuthContext } from 'contexts';
import type { Patient, Task } from 'fhir/r4';
import React from 'react';
import { Checkbox, Icon, Label, List, Popup } from 'semantic-ui-react';

import { TASK_ICON_COLORS } from '../fixtures';

import {
  PRIORITY_ICON_COLORS,
  PRIORITY_ICONS,
  TASK_DUE_DATE_COLORS,
  TASK_DUE_DATE_ICON,
  TASK_ICON_MAPS,
  TASK_STATUS_ICONS,
} from './fixtures';
import { getTaskBorderColor, getTaskDueDateStatus, getTaskOrganization, getVendorValue, openPreview } from './helpers';
import {
  DueDate,
  ListItemContainer,
  ListItemHeader,
  ListItemTitle,
  ListItemWrapper,
  TaskDetailsWrapper,
  TaskIconWrapper,
  TaskType,
} from './subListItem.styles';

type SubListItemProps = {
  currentTaskId?: string | undefined;
  handleDisableMultiSelect: (isDisabled: boolean) => void;
  handleSelectTask: (taskId: string | undefined) => void;
  handleSelectTasks: (tasks: Task[]) => void;
  selectedTasks: Task[];
  task: Task;
  fhirPatient?: Patient | undefined;
};

const SubListItem: React.FC<SubListItemProps> = ({
  currentTaskId,
  handleDisableMultiSelect,
  handleSelectTask,
  handleSelectTasks,
  selectedTasks,
  task,
  fhirPatient,
}) => {
  const coding = task.code?.coding?.[0];
  const vendorValue = getVendorValue(task);
  const referralId = task.groupIdentifier?.value;
  const { duedate, status, taskDueDateStatus } = getTaskDueDateStatus(task);
  const isSelected = selectedTasks.some((t) => t.id === task.id);
  const authInfo = useAuthContext();
  const orgDisplay = authInfo.payers.find((obj) => obj.key === getTaskOrganization(task))?.text; //get organization display name

  const handleTaskOnClick = () => {
    handleSelectTask(task.id);
  };

  const handleMultipleTasks = (task: Task) => {
    const isTaskSelected = selectedTasks.some((selectedTask) => selectedTask.id === task.id);
    const updatedTasks = isTaskSelected
      ? selectedTasks.filter((selectedTask) => selectedTask.id !== task.id)
      : [...selectedTasks, task];

    handleDisableMultiSelect(updatedTasks.length === 0);
    handleSelectTasks(updatedTasks);
  };

  const isCompleted = status === 'completed';

  return (
    <ListItemContainer>
      <List.Item
        as="span"
        onClick={handleTaskOnClick}
        style={{
          cursor: 'pointer',
          backgroundColor: currentTaskId === task.id ? '#add8e6' : 'white',
          padding: '10px',
          borderRadius: '6px',
          margin: '16px 0px',
          boxShadow: '0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12)',
          border: getTaskBorderColor(task, taskDueDateStatus),
          width: '100%',
          display: 'flex',
          gap: 6,
        }}
      >
        <TaskIconWrapper>
          {coding?.code && (
            // Asserted, not defaulted: an unmapped code must keep rendering the icon
            // with no name/colour rather than acquiring a fallback one.
            <List.Icon size="large" name={TASK_ICON_MAPS[coding.code]!} color={TASK_ICON_COLORS[coding.code]!} />
          )}
        </TaskIconWrapper>
        <TaskDetailsWrapper>
          <ListItemHeader>
            <ListItemWrapper>
              {vendorValue && <ListItemTitle>{vendorValue}</ListItemTitle>}
              <ListItemTitle>{coding?.display}</ListItemTitle>
              {task.owner?.display && (
                <span>
                  <b>-</b> {task.owner?.display}
                </span>
              )}
            </ListItemWrapper>
            <ListItemWrapper>
              {task.priority === 'asap' && (
                <Icon style={{ marginTop: -5 }} name={PRIORITY_ICONS['asap']!} color={PRIORITY_ICON_COLORS['asap']!} />
              )}
              {!isCompleted && taskDueDateStatus && (
                <Icon
                  style={{ marginTop: -5 }}
                  name={TASK_DUE_DATE_ICON[taskDueDateStatus]!}
                  color={TASK_DUE_DATE_COLORS[taskDueDateStatus]!}
                />
              )}
              <DueDate style={{ color: !isCompleted ? TASK_DUE_DATE_COLORS[taskDueDateStatus] : 'inherit' }}>
                {duedate}
              </DueDate>
            </ListItemWrapper>
          </ListItemHeader>
          <List.Content>
            <List.Description style={{ marginTop: -3, marginBottom: 5 }}>
              <TaskType>{task.reasonCode?.text?.split('|')[0] ?? ''}</TaskType>
            </List.Description>
            <List.Description>
              <span style={{ whiteSpace: 'pre-wrap' }}>{task.description}</span>
              <p style={{ marginTop: 5 }}>
                {orgDisplay && (
                  <Label basic color="green" size="tiny">
                    {orgDisplay}
                  </Label>
                )}{' '}
                {referralId && (
                  <Label basic color="red" size="tiny">
                    {referralId}
                  </Label>
                )}
                <span style={{ float: 'right' }}>
                  {coding?.code === 'automated' && (
                    <Popup
                      trigger={<Icon name="file outline" />}
                      wide="very"
                      content={openPreview(task, fhirPatient)}
                      on="hover"
                      position="bottom right"
                    />
                  )}
                  <img width={24} height={24} src={TASK_STATUS_ICONS[task.status]} alt={task.status} />
                </span>
              </p>
            </List.Description>
          </List.Content>
        </TaskDetailsWrapper>
      </List.Item>
      <span>
        <Checkbox
          style={{ width: '15px', height: '15px' }}
          onClick={() => handleMultipleTasks(task)}
          checked={isSelected}
        />
      </span>
    </ListItemContainer>
  );
};

export default SubListItem;
